import React, { useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash';

import i18n from '@nextroll/ar-i18n';
import { useContainerContext } from '@nextroll/ar-container-context';
import { RWListRules } from '@nextroll/ar-rollworks-list-builder';

import { IWorkflowSchemaTriggerExclusions } from 'types/rofl';
import { useRoflStore } from 'store/roflStore';
import { WorkflowTriggerNode } from 'store/nodeSlice';

import { TriggerNodeBase, INodeBaseProps } from '../../basic/NodeBase/NodeBase';
import {
    TriggerListBuilderSection, IAccountListBuilderExclusions,
} from './AccountTriggerNodeSection/TriggerListBuilderSection';


interface IAccountTriggerData {
    config: {
        list_rule?: RWListRules | undefined;
        rulesHaveErrors?: boolean;
    } & IWorkflowSchemaTriggerExclusions;
}

export const AccountTriggerNode = (props: INodeBaseProps<WorkflowTriggerNode>) => {
    const { advertisable: { eid: advertisableEid } = {}, user } = useContainerContext();

    const [triggerSchemaConfig, updateTriggerResults, shouldShowRequiredError] = useRoflStore(state => [
        (state.contentData.trigger?.data as IAccountTriggerData)?.config,
        state.updateTriggerResults,
        state.shouldShowRequiredError,
    ]);

    // local data; 'original' for previous changes vs. 'current' for unsaved changes
    const [triggerConfig, setTriggerConfig] = useState({
        original: undefined,
        current: { config: { list_rule: undefined as RWListRules | undefined, rulesHaveErrors: false } },
    } as { original?: IAccountTriggerData; current?: IAccountTriggerData } | undefined);

    /**
     * memorize account list builder props to avoid throttling
     */
    const triggerSchemaExclusions = useMemo(() => ({
        notInCrmAccounts: triggerSchemaConfig?.not_in_crm_accounts || false,
        notInSalesMarketing: triggerSchemaConfig?.not_in_sales_marketing || false,
        notInAdvertisingFilter: triggerSchemaConfig?.not_in_advertising_filter || false,
        notInIntent: triggerSchemaConfig?.not_in_intent || false,
        generalExclusionsTypes: triggerSchemaConfig?.exclusions_types || [],
        excludedAccounts: triggerSchemaConfig?.excluded_accounts || [],
    }), [triggerSchemaConfig]);
    const handleListBuilderUpdate = useCallback((
        rules: RWListRules | null,
        rulesValid: boolean,
        exclusions?: IAccountListBuilderExclusions | null
    ) => {
        if (!rules) {
            return; // empty rules; do nothing
        }
        const currentExclusions = {
            notInCrmAccounts: triggerConfig?.current?.config?.not_in_crm_accounts,
            notInSalesMarketing: triggerConfig?.current?.config?.not_in_sales_marketing,
            notInAdvertisingFilter: triggerConfig?.current?.config?.not_in_advertising_filter,
            notInIntent: triggerConfig?.current?.config?.not_in_intent,
            generalExclusionsTypes: triggerConfig?.current?.config?.exclusions_types,
            excludedAccounts: triggerConfig?.current?.config?.excluded_accounts,
        };
        if (isEqual(rules, triggerConfig?.current?.config?.list_rule) && isEqual(exclusions, currentExclusions)) {
            return; // same rules and exclusions; do nothing
        }
        const currentConfig = {
            config: {
                list_rule: rules || undefined,
                rulesHaveErrors: !rulesValid,

                // naming conversions are different between in the list builder component vs. the API endpoint
                not_in_crm_accounts: exclusions?.notInCrmAccounts,
                not_in_sales_marketing: exclusions?.notInSalesMarketing,
                not_in_advertising_filter: exclusions?.notInAdvertisingFilter,
                not_in_intent: exclusions?.notInIntent,
                exclusions_types: exclusions?.generalExclusionsTypes,
                excluded_accounts: exclusions?.excludedAccounts,
            }
        };
        setTriggerConfig(previousConfig => ({ ...previousConfig, current: currentConfig }));
    }, [triggerConfig, setTriggerConfig]);

    /**
     * disperse the list builder components to put them piecewisely, only when the feature flag is on
     */
    const {
        banners: TriggerListBanners,
        builder: TriggerListBuilder,
        preview: TriggerListPreview,
        exclusions: TriggerListExclusions,
        btnPreview: TriggerListBuilderBtnPreview,
        isLoading: isTriggerListBuilderLoading,
        isValid: isTriggerListBuilderValid,
        results: triggerListBuilderResults,
    } = TriggerListBuilderSection({
        advertisableEid,
        user,
        rules: triggerSchemaConfig?.list_rule,
        exclusions: triggerSchemaExclusions,
        onUpdate: handleListBuilderUpdate,
        validationResult: undefined, // TODO: possibly to read from existing data
    });

    /**
     * total number of accounts used in the trigger node content
     */
    const totalAccounts = useMemo(() => {
        triggerListBuilderResults && updateTriggerResults(triggerListBuilderResults); // keep a copy of the builder results in the store
        return triggerListBuilderResults?.totalCount;
    }, [triggerListBuilderResults, updateTriggerResults]);

    const panelElementConfigs = useMemo(() => {
        const triggerListBuilderSection = () => (
            <>
                {TriggerListBanners}
                {TriggerListBuilder}
                {TriggerListPreview}
                {TriggerListExclusions}
                {shouldShowRequiredError && !triggerConfig?.current?.config?.list_rule && (
                    <p className='text-danger'>
                        <i className='ar-icon ar-icon-fw fa-exclamation-circle mr-1' />
                        {i18n.gettext('Trigger rules cannot be empty.')}
                    </p>
                )}
            </>
        );
        return [
            {
                key: 'workload-trigger-node-panel-element-account-builder',
                component: triggerListBuilderSection,
                isRequired: true,
                validate: () => !!triggerConfig?.current?.config?.list_rule && isTriggerListBuilderValid,
            },
        ];
    }, [
        TriggerListBanners, TriggerListBuilder, TriggerListPreview, TriggerListExclusions,
        shouldShowRequiredError, triggerConfig, isTriggerListBuilderValid,
    ]);

    return (
        <TriggerNodeBase<IAccountTriggerData>
            {...props}
            panelSubtitle={i18n.gettext('Trigger this workflow when Accounts match these criteria:')}
            panelElementConfigs={panelElementConfigs}
            panelClassName='workflow-account-trigger-node-panel'
            loading={isTriggerListBuilderLoading}
            content={triggerConfig}
            setContent={setTriggerConfig}
            warning={props.warning}
            error={props.error}
            actionButtons={[React.cloneElement(TriggerListBuilderBtnPreview, { key: 'workflow-node-panel-btn-preview' })]}
        >
            <p>{i18n.gettext('When Accounts meet the following criteria:')}</p>
            <div className='workflow-contact-trigger-node-title-container'>
                <strong>{i18n.gettext('One or more custom criteria')}</strong>
            </div>
            <a type='button'>{i18n.gettext('Edit')}</a>
            {totalAccounts !== undefined && (
                <div className='small workflow-contact-trigger-node-num-of-contacts'>
                    {i18n.ngettext(
                        '<TOTAL /> account meet the trigger criteria at this time.',
                        '<TOTAL /> accounts meet the trigger criteria at this time.',
                        totalAccounts || 0,
                        { TOTAL: <strong>{i18n.numeral(totalAccounts).format('0,0')}</strong> },
                    )}
                </div>
            )}
        </TriggerNodeBase>
    );
};
